import { DatePicker, TimePicker, Checkbox, Radio } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import Swal from "sweetalert2";

import { Button } from "@components/Button";
import ErrorValidate from "@components/ErrorValidate";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import {
  COLOR_DARK,
  COLOR_GREEN,
  COLOR_RED,
  COLOR_WHITE,
} from "@static/COLORS";
import { fetchAPI } from "@utils/https/fetchAPI";
import { VALIDATE_RULES } from "@validate/index";

const { RangePicker } = DatePicker;
const { Group: RadioGroup } = Radio;

const FormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 2px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

const SectionContainerDayOfWeeks = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  justify-content: center;
  margin-bottom: 16px;
`;

const SectionContainerTime = styled.div`
  display: flex;
  gap: 20px;
`;

const GroupInput = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OPTION_STATUS = [
  { label: "Draft", value: "DRAFT" },
  { label: "On Air", value: "ON-AIR" },
];

const DAY_OF_WEEK_LIST = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const FormCreate = ({
  customerId,
  locationId,
  setDataList = () => undefined,
  setIsModalVisible,
  dataList,
}) => {
  const navigate = useNavigate();
  const [isNoEnd, setIsNoEnd] = useState(false);
  const [isAllTime, setIsAllTime] = useState(false);
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      status: "ON-AIR",
      daysOfWeek: {
        sun: true,
        mon: true,
        tue: true,
        wed: true,
        thu: true,
        fri: true,
        sat: true,
      },
    },
  });

  const onHandleCreate = async (data) => {
    const name = _.get(data, ["name"]);
    const days_of_week = _.get(data, ["daysOfWeek"]);
    const start_time = dayjs(_.get(data, ["startTime"])).format("HH:mm:ss");
    const end_time = dayjs(_.get(data, ["endTime"])).format("HH:mm:ss");
    const status = _.get(data, ["status"]);
    const width = _.get(data, ["width"]);
    const height = _.get(data, ["height"]);

    const date_range_start_raw = _.get(data, ["dateRange", 0]);
    const date_range_end_raw = _.get(data, ["dateRange", 1]);

    const start_date = dayjs(date_range_start_raw).format("YYYY-MM-DD");
    const end_date = dayjs(date_range_end_raw).format("YYYY-MM-DD");

    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/advertising-campaigns-create",
      isAuth: true,
      data: {
        name,
        days_of_week,
        start_time,
        end_time,
        status,
        width,
        height,
        start_date,
        end_date,
        category_customer_id: customerId,
        location_id: locationId,
      },
    });
    if (_.get(resp, ["status"])) {
      const results = _.chain(resp).get(["result"]).value();
      toast.success("Create Campaigns Success.");
      const updateData = _.chain(dataList)
        .thru((list) => {
          return [results, ...list];
        })
        .map((item, index) => {
          return { ...item, key: index + 1, index: index + 1 };
        })
        .value();
      setDataList(updateData);

      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));

      Swal.fire({
        title: "ต้องการไปที่\ncampaign block หรือไม่?",
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: COLOR_GREEN,
        cancelButtonColor: COLOR_RED,
        confirmButtonText: "ไป",
        cancelButtonText: "ไม่ไป",
        reverseButtons: true,
        customClass: {
          title: "custom-font-swal2",
          content: "custom-font-swal2",
          confirmButton: "custom-font-swal2",
          cancelButton: "custom-font-swal2",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          const customerId = _.get(results, ["category_customer_id"]);
          const locationId = _.get(results, ["location_id"]);
          const campaign_id = _.get(results, ["id"]);
          const campaign_name = _.get(results, ["name"]);
          const campaign_width = _.get(results, ["width"]);
          const campaign_height = _.get(results, ["height"]);
          const campaign_start_date = _.get(results, ["start_date"]);
          const campaign_end_date = _.get(results, ["end_date"]);
          const adsBlocks = _.get(results, ["adsBlocks"], []);

          navigate("/campaign-blocks", {
            state: {
              customerId,
              locationId,
              campaign_id,
              campaign_name,
              campaign_width,
              campaign_height,
              campaign_start_date,
              campaign_end_date,
              adsBlocks,
            },
          });
        }
      });
    }
  };

  const onHandleNoEndChange = (checked) => {
    setIsNoEnd(checked);
    if (checked) {
      setValue("dateRange", [dayjs("1990-01-01"), dayjs("3000-01-01")]);
    } else {
      setValue("dateRange", [
        dayjs().subtract(1, "day"),
        dayjs().add(1, "month"),
      ]);
    }
  };

  const onHandleAllTimeChange = (checked) => {
    setIsAllTime(checked);
    if (checked) {
      setValue("startTime", dayjs("00:00:00", "HH:mm:ss"));
      setValue("endTime", dayjs("23:59:59", "HH:mm:ss"));
    } else {
      setValue("startTime", dayjs("09:00:00", "HH:mm:ss"));
      setValue("endTime", dayjs("22:00:00", "HH:mm:ss"));
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onHandleCreate)} autoComplete="off">
      <Line />
      <InnerContainer style={{ overflow: "scroll" }}>
        <div>
          <Controller
            name="name"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Name
                </Text>
                <StyledInput
                  label="name"
                  {...field}
                  size="large"
                  placeholder="name"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <GroupInput>
            <Controller
              name="dateRange"
              control={control}
              rules={_.get(VALIDATE_RULES, ["required"])}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Text
                    color={COLOR_DARK}
                    fontSize="14px"
                    style={{ marginBottom: 4, marginLeft: 4 }}
                  >
                    Date Range
                  </Text>
                  <RangePicker
                    {...field}
                    disabled={isNoEnd}
                    className={isNoEnd && "picker-no-limit"}
                    style={{
                      width: "100%",
                      height: 40,
                    }}
                  />
                  <ErrorValidate justify="start" error={error} />
                </div>
              )}
            />

            <CheckboxContainer>
              <Text color={COLOR_DARK} fontSize="14px">
                Forever
              </Text>
              <Checkbox
                style={{ marginTop: 10 }}
                checked={isNoEnd}
                onChange={(e) => onHandleNoEndChange(e.target.checked)}
              />
            </CheckboxContainer>
          </GroupInput>
          <GroupInput>
            <SectionContainerTime>
              <Controller
                name="startTime"
                control={control}
                rules={_.get(VALIDATE_RULES, ["required"])}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ width: "100%" }}>
                    <Text
                      color={COLOR_DARK}
                      fontSize="14px"
                      style={{ marginBottom: 4, marginLeft: 4 }}
                    >
                      Start Time
                    </Text>
                    <TimePicker
                      className={isAllTime && "picker-no-limit"}
                      disabled={isAllTime}
                      style={{ width: "100%", height: 40 }}
                      {...field}
                      showNow={false}
                      needConfirm={false}
                      onChange={(value) => field.onChange(value)}
                    />
                    <ErrorValidate justify="start" error={error} />
                  </div>
                )}
              />

              <Controller
                name="endTime"
                control={control}
                rules={_.get(VALIDATE_RULES, ["required"])}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ width: "100%" }}>
                    <Text
                      color={COLOR_DARK}
                      fontSize="14px"
                      style={{ marginBottom: 4, marginLeft: 4 }}
                    >
                      End Time
                    </Text>
                    <TimePicker
                      className={isAllTime && "picker-no-limit"}
                      disabled={isAllTime}
                      showNow={false}
                      needConfirm={false}
                      style={{ width: "100%", height: 40 }}
                      {...field}
                      onChange={(value) => field.onChange(value)}
                    />
                    <ErrorValidate justify="start" error={error} />
                  </div>
                )}
              />
            </SectionContainerTime>
            <CheckboxContainer>
              <Text color={COLOR_DARK} fontSize="14px">
                All Time
              </Text>
              <Checkbox
                checked={isAllTime}
                style={{ marginTop: 10 }}
                onChange={(e) => onHandleAllTimeChange(e.target.checked)}
              />
            </CheckboxContainer>
          </GroupInput>

          <div>
            <Text
              color={COLOR_DARK}
              fontSize="14px"
              style={{ marginBottom: 4, marginLeft: 4 }}
            >
              Day of weeks
            </Text>

            <SectionContainerDayOfWeeks>
              {_.map(DAY_OF_WEEK_LIST, (item) => (
                <Controller
                  key={item}
                  name={`daysOfWeek.${_.toLower(item)}`}
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value}>
                      {item}
                    </Checkbox>
                  )}
                />
              ))}
            </SectionContainerDayOfWeeks>
          </div>

          <Controller
            name="width"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Width
                </Text>
                <StyledInput
                  label="width"
                  {...field}
                  size="large"
                  placeholder="width"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="height"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Height
                </Text>
                <StyledInput
                  label="height"
                  {...field}
                  size="large"
                  placeholder="height"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="status"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Status
                </Text>
                <RadioGroup
                  {...field}
                  options={OPTION_STATUS}
                  optionType="button"
                  style={{
                    borderColor: error ? "red" : undefined,
                  }}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
        </div>

        <ButtonContainer>
          <Button
            type="submit"
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Save
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </FormContainer>
  );
};
